import { useState, useEffect } from 'react';
import axios from 'axios';
import { History, Home, AlertTriangle, RotateCcw, Eye, ChevronLeft, ChevronRight, Search } from 'lucide-react';
import AnimalDetail from '../components/AnimalDetail';
import { useNavigate } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const placeholderImg = 'https://images.unsplash.com/photo-1563441405613-82b7d27cbc23?w=400&h=300&fit=crop';
const PER_PAGE = 16;
const fmtDate = (d) => { if (!d) return ''; const p = d.slice(0, 10).split('-'); return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : d; };

export default function HistoricoPage() {
  const [animais, setAnimais] = useState([]);
  const [tipo, setTipo] = useState('todos');
  const [pesq, setPesq] = useState('');
  const [pagina, setPagina] = useState(0);
  const [showRegresso, setShowRegresso] = useState(null);
  const [regressoData, setRegressoData] = useState({ data_regresso: '', motivo_volta: '' });
  const [selected, setSelected] = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  const navigate = useNavigate();

  const carregar = () => {
    axios.get(`${API}/animais?ativos=false`).then(r => {
      setAnimais(r.data.filter(a => a.saida));
    }).catch(console.error);
  };
  useEffect(() => { carregar(); }, []);

  const filtrados = animais
    .filter(a => tipo === 'todos' || a.motivo_saida === tipo)
    .filter(a => !pesq.trim() || a.nome?.toLowerCase().includes(pesq.toLowerCase()) || a.chip?.includes(pesq));

  const total = Math.ceil(filtrados.length / PER_PAGE);
  const visiveis = filtrados.slice(pagina * PER_PAGE, (pagina + 1) * PER_PAGE);

  const submeterRegresso = async () => {
    if (!showRegresso) return;
    await axios.put(`${API}/animais/${showRegresso.id}/regresso`, regressoData);
    setShowRegresso(null);
    carregar();
  };

  return (
    <div data-testid="historico-page">
      <h2 className="font-heading font-bold text-2xl mb-6 flex items-center gap-2">
        <History className="w-6 h-6 text-primary" /> Histórico de Animais
      </h2>

      <div className="flex flex-wrap gap-3 mb-6">
        <select value={tipo} onChange={e => { setTipo(e.target.value); setPagina(0); }}
          className="rounded-xl border border-border bg-card px-3 py-2 text-sm">
          <option value="todos">Todos</option>
          <option value="adocao">Adotados</option>
          <option value="falecimento">Falecidos</option>
          <option value="levantamento">Levantamentos</option>
        </select>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input value={pesq} onChange={e => { setPesq(e.target.value); setPagina(0); }}
            placeholder="Nome ou Chip" className="pl-9 pr-3 py-2 rounded-xl border border-border bg-card text-sm w-48" />
        </div>
      </div>

      {visiveis.length === 0 ? (
        <p className="text-muted-foreground text-center py-12">Nenhum animal neste criterio.</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
          {visiveis.map(a => {
            const fotoUrl = a.fotos?.length > 0 ? ((u) => (u && (u.startsWith('http') || u.startsWith('data:'))) ? u : `${process.env.REACT_APP_BACKEND_URL}${u}`)(a.fotos[0]) : placeholderImg;
            return (
              <div key={a.id} className="bg-card rounded-2xl border border-border overflow-hidden relative">
                <div className="absolute top-2 left-2 z-10">
                  {a.motivo_saida === 'adocao' && <span className="text-xs bg-green-600 text-white px-2 py-0.5 rounded-full flex items-center gap-1"><Home className="w-3 h-3" />Adotado</span>}
                  {a.motivo_saida === 'falecimento' && <span className="text-xs bg-gray-700 text-white px-2 py-0.5 rounded-full flex items-center gap-1"><AlertTriangle className="w-3 h-3" />Faleceu</span>}
                  {a.motivo_saida === 'levantamento' && <span className="text-xs bg-blue-600 text-white px-2 py-0.5 rounded-full">Levantamento</span>}
                </div>
                <img src={fotoUrl} alt={a.nome} className="w-full h-40 object-cover" onError={e => { e.target.src = placeholderImg; }} />
                <div className="p-3">
                  <h3 className="font-heading font-bold text-foreground">{a.nome}</h3>
                  <div className="text-xs text-muted-foreground space-y-0.5 mt-1">
                    <p><strong>Chip:</strong> {a.chip || 'N/D'}</p>
                    <p><strong>Entrada:</strong> {fmtDate(a.entrada)} <strong className="ml-1">Saída:</strong> {fmtDate(a.saida)}</p>
                    {a.motivo_saida === 'adocao' && a.dados_adotante && <p><strong>Adotante:</strong> {a.dados_adotante}</p>}
                    {a.tipo_falecimento && <p><strong>Tipo:</strong> {a.tipo_falecimento === 'eutanasia' ? 'Eutanasia' : 'Morte Natural'}</p>}
                  </div>
                  <div className="flex gap-1 mt-2">
                    <button onClick={() => { setSelected(a); setShowDetail(true); }} className="text-xs bg-muted text-foreground rounded-full px-2 py-1 flex items-center gap-1"><Eye className="w-3 h-3" />Ver</button>
                    {a.motivo_saida === 'adocao' && (
                      <button onClick={() => { setShowRegresso(a); setRegressoData({ data_regresso: '', motivo_volta: '' }); }}
                        className="text-xs bg-blue-100 text-blue-800 rounded-full px-2 py-1 flex items-center gap-1"><RotateCcw className="w-3 h-3" />Reentrada</button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {total > 1 && (
        <div className="flex justify-center items-center gap-2 mt-6">
          <button onClick={() => setPagina(p => Math.max(0, p - 1))} disabled={pagina === 0} className="p-2 rounded-full border border-border bg-card disabled:opacity-30"><ChevronLeft className="w-4 h-4" /></button>
          <span className="text-sm text-muted-foreground">Pagina {pagina + 1} de {total}</span>
          <button onClick={() => setPagina(p => Math.min(total - 1, p + 1))} disabled={pagina >= total - 1} className="p-2 rounded-full border border-border bg-card disabled:opacity-30"><ChevronRight className="w-4 h-4" /></button>
        </div>
      )}

      {showRegresso && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-card rounded-2xl border border-border p-6 w-full max-w-sm m-4 space-y-3">
            <h3 className="font-heading font-bold text-lg">Reentrada: {showRegresso.nome}</h3>
            <div><label className="text-xs text-muted-foreground">Data de Reentrada</label><input type="date" value={regressoData.data_regresso} onChange={e => setRegressoData({ ...regressoData, data_regresso: e.target.value })} className="w-full rounded-lg border border-border bg-background px-2 py-1.5 text-sm" /></div>
            <div><label className="text-xs text-muted-foreground">Motivo</label><textarea rows={2} value={regressoData.motivo_volta} onChange={e => setRegressoData({ ...regressoData, motivo_volta: e.target.value })} className="w-full rounded-lg border border-border bg-background px-2 py-1.5 text-sm" /></div>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setShowRegresso(null)} className="text-xs bg-muted text-foreground rounded-full px-3 py-1.5">Cancelar</button>
              <button onClick={submeterRegresso} className="text-xs bg-primary text-primary-foreground rounded-full px-3 py-1.5">Guardar</button>
            </div>
          </div>
        </div>
      )}

      {showDetail && selected && (
        <AnimalDetail animal={selected} onClose={() => { setShowDetail(false); setSelected(null); }} onUpdate={carregar} onEdit={(id) => navigate(`/editar/${id}`)} />
      )}
    </div>
  );
}
